import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BcSalesOrderFilterSchema,
  OrderStatusSchema,
  type BcSalesOrderFilter,
  type OrderStatus,
} from '@/lib/vision/schemas'
import { dateFilterSearchParams, parseDateFilter, type DateFilter } from './date-range'

/**
 * The URL owns the queue view. Filters, sort, and page all read/write to
 * query params so a link reproduces exactly what the sender saw — the same
 * contract the legacy Pulse UI enforces (see the `THE URL OWNS THE VIEW`
 * comment in `oms-po-ingest/pulse/src/screens/Pos.tsx`).
 *
 * Filter shape:
 *   * `channel`      — repeated marketplace families (`amazon`, `flipkart`). Vision's
 *                      canonical_orders stores this as `source_channel`; that
 *                      is the column the server actually filters on today.
 *                      Omitted (Channel = All) means every family. Do not
 *                      default to amazon.
 *   * `channelCode`  — the specific channel-account slug from Vision's
 *                      `channels` table (e.g. `bbpl-amazon-vc`). Sourced from
 *                      the new `/v1/channels` directory. NOTE: Vision's
 *                      `/v1/orders` endpoint does NOT yet key on this
 *                      column — extra query params fall through Spring
 *                      silently, so today this is a "UI-scope hint" that
 *                      survives in the URL for share/return, and a follow-up
 *                      Vision PR wires the server-side filter (canonical_orders
 *                      does not carry the channel-account link column yet).
 *   * `vendorCode`   — repeated vendor codes. Filters against Vision's
 *                      `vendors` directory. UI-scope hint until the Vision
 *                      follow-up lands.
 *   * `range`        — Pulse v1 time pill: `all` | `7d` | `30d` | `90d` |
 *                      `custom`. Default `all` ("All loaded") — Vision applies
 *                      a rolling 180-day floor on PO date. Custom also writes
 *                      `from` / `to` (YYYY-MM-DD).
 *   * `bcSalesOrder` — repeated, like `status`. Where the PO stands on Business
 *                      Central Sales Orders across its waves: `all` |
 *                      `partial` | `none` | `unknown` | `no-waves`. Vision
 *                      filters server-side off its own projection, so this
 *                      param is the whole feature — the list carries no per-row
 *                      BC state and the queue makes no Heimdall call. A future
 *                      `bcInvoice` / `irn` key slots in the same way once that
 *                      data exists on the Boldfit side.
 */

export interface OrdersUrlState {
  q: string
  channels: string[]
  channelCode: string | null
  vendorCodes: string[]
  statuses: OrderStatus[]
  bcSalesOrder: BcSalesOrderFilter[]
  date: DateFilter
  page: number
  size: number
  sort: string
}

type UrlPatchValue =
  string | string[] | number | OrderStatus[] | BcSalesOrderFilter[] | DateFilter | null

export type OrdersUrlPatch = Partial<Record<keyof OrdersUrlState, UrlPatchValue>>

const DEFAULTS = {
  page: 0,
  size: 25,
  sort: '-orderedAt',
} as const

/** Vision pages `createdAt` on ingest `received_at`. */
export const SERVER_SORT_FIELD = 'createdAt'
/** Vision pages `orderedAt` on `external_po_date` (Amazon purchaseOrderDate). */
export const SERVER_PO_DATE_SORT_FIELD = 'orderedAt'
export const SERVER_DEFAULT_SORT = '-createdAt'
/** UI default: PO datetime descending (`orderedAt` is the #16 PO date column). */
export const DEFAULT_SORT = DEFAULTS.sort

export type SortDir = 'asc' | 'desc'

export function parseSortParam(sort: string): { field: string; dir: SortDir } {
  const descending = sort.startsWith('-')
  return {
    field: descending ? sort.slice(1) : sort,
    dir: descending ? 'desc' : 'asc',
  }
}

/** PO detail path. A selected channel stays on the URL so Back returns to that queue. */
export function orderDetailHref(externalOrderNumber: string, channel?: string | null): string {
  const path = `/pos/${encodeURIComponent(externalOrderNumber)}`
  if (!channel) return path
  return `${path}?channel=${encodeURIComponent(channel)}`
}

/** Queue path. All channels is `/pos`; a family filter is `/pos?channel=…`. */
export function ordersListHref(channel?: string | null): string {
  if (!channel) return '/pos'
  return `/pos?channel=${encodeURIComponent(channel)}`
}

/**
 * Map the URL sort to what Vision accepts. `createdAt` and `orderedAt` are
 * server-side; anything else is remapped to createdAt-desc and the current
 * page is reordered client-side (see `sortOrderRows`).
 */
export function visionSortParam(sort: string): string {
  const { field } = parseSortParam(sort)
  if (field === SERVER_SORT_FIELD || field === SERVER_PO_DATE_SORT_FIELD) return sort
  return SERVER_DEFAULT_SORT
}

/**
 * Header-click cycle: first click ascending, second descending, third
 * clears back to the default (`-orderedAt`). When the clicked column *is* the
 * default field already in desc, toggle to asc instead of clearing (clearing
 * would restore the same default).
 *
 * `defaultSort` is a parameter because the PO line grid runs the same cycle
 * against its own default (see `DEFAULT_LINE_SORT`).
 */
export function cycleSortParam(
  current: string,
  field: string,
  defaultSort: string = DEFAULT_SORT,
): string | null {
  const parsed = parseSortParam(current)
  if (parsed.field !== field) return field
  if (parsed.dir === 'asc') return `-${field}`
  if (defaultSort === `-${field}`) return field
  return null
}

export function useOrdersUrlState(): [OrdersUrlState, (patch: OrdersUrlPatch) => void] {
  const [params, setParams] = useSearchParams()

  const state: OrdersUrlState = useMemo(
    () => ({
      q: params.get('q') ?? '',
      channels: parseTokens(params.getAll('channel')),
      channelCode: params.get('channelCode'),
      vendorCodes: parseTokens(params.getAll('vendorCode')),
      statuses: parseStatuses(params.getAll('status')),
      bcSalesOrder: parseBcSalesOrder(params.getAll('bcSalesOrder')),
      date: parseDateFilter(params),
      page: clampInt(params.get('page'), DEFAULTS.page, { min: 0 }),
      size: clampInt(params.get('size'), DEFAULTS.size, { min: 10, max: 100 }),
      sort: params.get('sort') ?? DEFAULTS.sort,
    }),
    [params],
  )

  const update = useCallback(
    (patch: OrdersUrlPatch) => {
      setParams(prev => {
        const next = new URLSearchParams(prev)
        for (const [key, value] of Object.entries(patch)) {
          if (key === 'date') {
            const written = dateFilterSearchParams(
              value && typeof value === 'object' && 'preset' in value ? value : { preset: 'all' },
            )
            for (const [param, raw] of Object.entries(written)) {
              if (raw == null) next.delete(param)
              else next.set(param, raw)
            }
            continue
          }
          const param =
            key === 'statuses'
              ? 'status'
              : key === 'channels'
                ? 'channel'
                : key === 'vendorCodes'
                  ? 'vendorCode'
                  : key
          if (value == null || (typeof value === 'string' && value === '')) {
            next.delete(param)
            continue
          }
          if (Array.isArray(value)) {
            next.delete(param)
            for (const entry of value) next.append(param, String(entry))
            continue
          }
          next.set(key, String(value))
        }
        // Any filter change resets the page — otherwise a filter would silently
        // land on an empty last-page.
        if (!('page' in patch)) next.delete('page')
        return next
      })
    },
    [setParams],
  )

  return [state, update]
}

function parseStatuses(raw: string[]): OrderStatus[] {
  const out: OrderStatus[] = []
  for (const value of raw) {
    for (const token of value.split(',')) {
      const parsed = OrderStatusSchema.safeParse(token.trim())
      if (parsed.success) out.push(parsed.data)
    }
  }
  return out
}

function parseTokens(raw: string[]): string[] {
  const out: string[] = []
  for (const value of raw) {
    for (const token of value.split(',')) {
      const normalized = token.trim()
      if (normalized && !out.includes(normalized)) out.push(normalized)
    }
  }
  return out
}

/**
 * Same shape as `parseStatuses`: repeated or comma-joined, unknown tokens
 * dropped rather than throwing, so a hand-edited or stale shared link still
 * loads the queue instead of blanking it.
 */
function parseBcSalesOrder(raw: string[]): BcSalesOrderFilter[] {
  const out: BcSalesOrderFilter[] = []
  for (const value of raw) {
    for (const token of value.split(',')) {
      const parsed = BcSalesOrderFilterSchema.safeParse(token.trim())
      if (parsed.success && !out.includes(parsed.data)) out.push(parsed.data)
    }
  }
  return out
}

function clampInt(
  raw: string | null,
  fallback: number,
  bounds: { min?: number; max?: number } = {},
): number {
  if (raw == null) return fallback
  const parsed = Number.parseInt(raw, 10)
  if (Number.isNaN(parsed)) return fallback
  if (bounds.min != null && parsed < bounds.min) return bounds.min
  if (bounds.max != null && parsed > bounds.max) return bounds.max
  return parsed
}
